import axios from 'axios';
import type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import Cookies from 'js-cookie';
import { env } from '@repo/settings';

// ============================================================
// Token Management - stored in memory and cookies
// ============================================================

let globalPortal: string | undefined;
let accessToken: string | null = null;
let refreshTokenValue: string | null = null;

/**
 * Set access token in memory and cookies
 */
export const setAccessToken = (token: string | null) => {
  accessToken = token;
  if (token) {
    Cookies.set(`${globalPortal}AccessToken`, token, {
      secure: window.location.protocol === 'https:',
      sameSite: 'strict',
      expires: 30, // Set cookie to expire in 30 days (persists across sessions)
    });
  } else {
    Cookies.remove(`${globalPortal}AccessToken`);
  }
};

/**
 * Get access token from memory or cookies
 */
export const getAccessToken = () => {
  if (accessToken) return accessToken;
  return Cookies.get(`${globalPortal}AccessToken`) || null;
};

/**
 * Set refresh token in memory and cookies
 */
export const setRefreshToken = (token: string | null) => {
  refreshTokenValue = token;
  if (token) {
    Cookies.set(`${globalPortal}RefreshToken`, token, {
      secure: window.location.protocol === 'https:',
      sameSite: 'strict',
      expires: 30, // Set cookie to expire in 30 days (persists across sessions)
    });
  } else {
    Cookies.remove(`${globalPortal}RefreshToken`);
  }
};

/**
 * Get refresh token from memory or cookies
 */
export const getRefreshToken = () => {
  if (refreshTokenValue) return refreshTokenValue;
  return Cookies.get(`${globalPortal}RefreshToken`) || null;
};

// ============================================================
// Unified API Client with Auto Token Refresh
// ============================================================

// Global refresh promise to prevent concurrent refresh requests
let refreshPromise: Promise<string | null> | null = null;

export const initPortal = (portal: string) => {
  globalPortal = portal;
  api = createApiClient();
};

const createApiClient = (config?: AxiosRequestConfig): AxiosInstance => {
  const instance = axios.create({
    baseURL: env.VITE_API_BASE_URL,
    timeout: 10000,
    headers: {
      'Content-Type': 'application/json',
    },
    withCredentials: true,
    ...config,
  });

  // Request interceptor: Attach access token to every request
  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    // Always read the latest access token from cookies
    const currentAccessToken = Cookies.get(`${globalPortal}AccessToken`) || null;

    if (currentAccessToken && config.headers) {
      config.headers.Authorization = `Bearer ${currentAccessToken}`;
    }

    // Add Accept-Language header
    if (config.headers) {
      const lang =
        typeof window !== 'undefined' ? window.localStorage.getItem('app_language') || 'en' : 'en';
      config.headers['Accept-Language'] = lang;
    }

    return config;
  });

  // Response interceptor: Handle 401/403 errors, token refresh, and 429 rate-limit retries
  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: any) => {
      const originalRequest = error.config;

      // ── 429 Too Many Requests — exponential backoff retry (max 2 attempts) ──
      if (error.response?.status === 429) {
        originalRequest._rateLimitRetries = (originalRequest._rateLimitRetries ?? 0) + 1;

        if (originalRequest._rateLimitRetries <= 2) {
          // Backoff: 1st retry after 1 s, 2nd after 2 s
          const delay = originalRequest._rateLimitRetries * 1000;
          await new Promise((resolve) => setTimeout(resolve, delay));
          return instance(originalRequest);
        }

        // Exhausted retries — notify the UI with a custom event so a toast can be shown
        window.dispatchEvent(
          new CustomEvent('api:rate-limited', {
            detail: { url: originalRequest.url },
          })
        );
        return Promise.reject(error);
      }

      // ── 401 / 403 — token refresh flow ────────────────────────────────────
      if (error.response?.status === 401 || error.response?.status === 403) {
        // Skip redirect for auth requests that should surface errors directly
        if (
          originalRequest.url?.includes('/auth/signin') ||
          originalRequest.url?.includes('/auth/change-password') ||
          originalRequest.url?.includes('/auth/refresh-token')
        ) {
          return Promise.reject(error);
        }

        const currentRefreshToken = Cookies.get(`${globalPortal}RefreshToken`) || null;

        // If no refresh token, clear tokens and signal AuthProvider to log out
        if (!currentRefreshToken) {
          setAccessToken(null);
          setRefreshToken(null);
          window.dispatchEvent(new Event('auth:logout'));
          return Promise.reject(error);
        }

        // If request hasn't been retried yet, try to refresh
        if (!originalRequest._retry) {
          originalRequest._retry = true;

          // If there's no ongoing refresh, start one
          if (!refreshPromise) {
            const targetPortal = globalPortal;
            // Read the LATEST cookie right before firing — avoids using a stale
            // value that was captured before a concurrent refresh updated it.
            const latestRefreshToken = Cookies.get(`${globalPortal}RefreshToken`) || null;
            // Use axios directly to avoid triggering interceptors
            refreshPromise = axios
              .post(
                `${env.VITE_API_BASE_URL}/${targetPortal}/auth/refresh-token`,
                { refreshToken: latestRefreshToken },
                {
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  withCredentials: true,
                }
              )
              .then((res: AxiosResponse) => {
                const newToken = res.data.accessToken;
                const newRefreshToken = res.data.refreshToken;

                setAccessToken(newToken);
                if (newRefreshToken) {
                  setRefreshToken(newRefreshToken);
                }

                refreshPromise = null;
                return newToken;
              })
              .catch(() => {
                setAccessToken(null);
                setRefreshToken(null);
                refreshPromise = null;
                window.dispatchEvent(new Event('auth:logout'));
                return null;
              });
          }

          // Wait for the refresh to complete
          const token = await refreshPromise;

          if (token) {
            // Update the original request with the new token and retry
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return instance(originalRequest);
          }
        }
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

// Initialized by initPortal() — called from AuthProvider on mount.
// All service files share this single instance via ES module live binding.
export let api: AxiosInstance;

export type { AxiosInstance, AxiosRequestConfig, AxiosResponse };
export { axios, createApiClient };
